import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from '@iconify/react';
import api from '@/services/apiService';
import { productService } from '@/services/productService';
import { ProductApiService } from '@/services/productApiService';
import { useToast } from '@/hooks/useToast';
import { getArenaSlug } from '@/utils/arena';

interface ServiceItem {
  id: number;
  sku: string;
  slug: string;
  name: string;
  description?: string;
  category_name?: string;
  price: number;
  quantity: number;
  images: string[];
  is_active: boolean;
  raw?: any;
}

const ServiceList: React.FC = () => {
  const toast = useToast();
  const arenaSlug = getArenaSlug() || localStorage.getItem('arenaSlug') || '';

  // Data state
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modal State (Add / Edit)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingItem, setEditingItem] = useState<ServiceItem | null>(null);

  // Form Fields
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrice, setFormPrice] = useState<number>(0);
  const [formQuantity, setFormQuantity] = useState<number>(0);
  const [formIsActive, setFormIsActive] = useState(true);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<ServiceItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch Services from API
  const loadServices = async () => {
    if (!arenaSlug) return;
    try {
      setLoading(true);
      const url = `/arenas/${arenaSlug}/services`;

      const params: Record<string, any> = { page, limit: perPage };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await api.get(url, { params });
      const payload = res.data?.data || {};

      const items = (payload.services || []).map((it: any) => ({
        id: it.id,
        sku: it.sku || '',
        slug: it.slug || '',
        name: it.name || '',
        description: it.description || '',
        category_name: it.category_name || '',
        price: Number(it.price) || 0,
        quantity: Number(it.quantity) || 0,
        images: Array.isArray(it.images) ? it.images : [],
        is_active: Boolean(it.is_active),
        raw: it,
      }));

      const pagination = payload.pagination || {};
      setServices(items);
      setTotalPages(pagination.total_pages || 1);
      setTotalItems(pagination.total ?? items.length);
    } catch (err) {
      console.error('Failed to load services', err);
      toast.error('Gagal memuat daftar sewa & layanan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [arenaSlug, page, perPage, search, statusFilter]);

  // Compute status counts
  const counts = useMemo(() => {
    const active = services.filter((s) => s.is_active).length;
    const inactive = services.filter((s) => !s.is_active).length;
    return {
      all: totalItems,
      active,
      inactive,
    };
  }, [services, totalItems]);

  // Toggle Item Active/Inactive
  const handleToggleStatus = async (item: ServiceItem) => {
    const newStatus = !item.is_active;
    try {
      await ProductApiService.updateProductVisibility(item.id, newStatus);
      setServices((prev) =>
        prev.map((s) => (s.id === item.id ? { ...s, is_active: newStatus } : s))
      );
      toast.success(`"${item.name}" berhasil di${newStatus ? 'aktifkan' : 'nonaktifkan'}`);
    } catch (err) {
      console.error('Failed to toggle status:', err);
      toast.error('Gagal memperbarui status');
    }
  };

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setFormMode('create');
    setEditingItem(null);
    setFormName('');
    setFormDescription('');
    setFormPrice(0);
    setFormQuantity(0);
    setFormIsActive(true);
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingImage(null);
    setIsFormModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item: ServiceItem) => {
    setFormMode('edit');
    setEditingItem(item);
    setFormName(item.name || '');
    setFormDescription(item.description || '');
    setFormPrice(item.price || 0);
    setFormQuantity(item.quantity || 0);
    setFormIsActive(item.is_active);
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingImage(item.images?.[0] || null);
    setIsFormModalOpen(true);
  };

  // Handle Photo File Selection
  const handlePhotoSelect = (file: File) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Handle Remove Photo
  const handleRemovePhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle Form Submit (Add or Edit)
  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('Nama sewa & layanan wajib diisi');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, any> = {
        name: formName.trim(),
        category_id: '2',
        description: formDescription.trim(),
        buy_price: formPrice,
        rent_price: formPrice,
        stock_qty: formQuantity,
        is_active: formIsActive,
      };

      if (formMode === 'create') {
        const files = photoFile ? [photoFile] : [];
        const res = await productService.createService(arenaSlug, payload, files);
        if (res && res.success) {
          toast.success('Sewa & layanan berhasil ditambahkan');
          setIsFormModalOpen(false);
          loadServices();
        } else {
          toast.error(res?.message || 'Gagal menambahkan sewa & layanan');
        }
      } else if (editingItem) {
        const newFiles = photoFile ? [photoFile] : [];
        const keepImages = existingImage ? [existingImage] : [];
        const res = await productService.updateProduct(
          arenaSlug,
          editingItem.slug,
          payload,
          newFiles,
          keepImages
        );
        if (res && res.success) {
          toast.success('Sewa & layanan berhasil diperbarui');
          setIsFormModalOpen(false);
          loadServices();
        } else {
          toast.error(res?.message || 'Gagal memperbarui sewa & layanan');
        }
      }
    } catch (err: any) {
      console.error('Error saving service:', err);
      toast.error(err?.message || 'Terjadi kesalahan saat menyimpan');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Delete Modal
  const handleOpenDeleteModal = (item: ServiceItem) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    setIsDeleting(true);
    try {
      await ProductApiService.deleteProduct(deletingItem.slug);
      toast.success(`"${deletingItem.name}" berhasil dihapus`);
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
      loadServices();
    } catch (err: any) {
      console.error('Failed to delete service:', err);
      toast.error(err?.message || 'Gagal menghapus sewa & layanan');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-12">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Sewa & Layanan</h1>
        </div>

        <button
          type="button"
          onClick={handleOpenCreateModal}
          className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white px-5 py-2.5 text-sm font-semibold shadow-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer self-start sm:self-auto"
        >
          <Icon icon="ph:plus-bold" className="w-4 h-4" />
          <span>Tambah Sewa & Layanan</span>
        </button>
      </div>

      {/* Main Card Wrapper */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white shadow-xs overflow-hidden">
        {/* Card Header Toolbar: Search & Status Filters */}
        <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Search Input */}
          <div className="relative w-full sm:w-72">
            <Icon
              icon="ph:magnifying-glass"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Cari sewa atau layanan"
              className="w-full rounded-full border border-[#E0E6ED] bg-white pl-10 pr-4 py-2 text-xs sm:text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
            />
          </div>

          {/* Status Filter Badges */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setStatusFilter('all');
                setPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                statusFilter === 'all'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Semua {counts.all}
            </button>

            <button
              type="button"
              onClick={() => {
                setStatusFilter('active');
                setPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                statusFilter === 'active'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Aktif {counts.active}
            </button>

            <button
              type="button"
              onClick={() => {
                setStatusFilter('inactive');
                setPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                statusFilter === 'inactive'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Nonaktif {counts.inactive}
            </button>
          </div>
        </div>

        {/* Content area: Loading / Empty / Table */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin" />
            <span className="text-sm text-gray-500">Memuat sewa & layanan...</span>
          </div>
        ) : services.length === 0 ? (
          <div className="py-16 text-center space-y-3 px-4">
            <div className="w-14 h-14 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center mx-auto text-[#FC480D]">
              <Icon icon="ph:package-bold" className="w-7 h-7" />
            </div>
            <h3 className="text-sm font-semibold text-[#0E1726]">Belum ada sewa & layanan</h3>
            <p className="text-xs text-[#8C8C8C] max-w-xs mx-auto">
              Tambahkan peralatan sewa atau layanan tambahan untuk disewakan kepada pelanggan Anda.
            </p>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              className="mt-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white px-5 py-2 text-xs font-semibold transition-colors inline-flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
              <span>Tambah Sekarang</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse text-left">
              <thead>
                <tr className="bg-[#F8F9FA] border-y border-[#F0F2F5] text-xs font-semibold text-[#0E1726]">
                  <th className="py-3 px-6 text-left font-semibold">Sewa & layanan</th>
                  <th className="py-3 px-6 text-center font-semibold">Ketersediaan</th>
                  <th className="py-3 px-6 text-left font-semibold">Harga</th>
                  <th className="py-3 px-6 text-center font-semibold">Status</th>
                  <th className="py-3 px-6 text-right font-semibold">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F2F5]">
                {services.map((item) => {
                  const imageSrc = item.images?.[0] || null;

                  return (
                    <tr key={item.id} className="hover:bg-gray-50/70 transition-colors">
                      {/* Sewa & Layanan Column */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3.5">
                          <div className="w-12 h-12 rounded-xl border border-[#E0E6ED] bg-[#F6F6F6] flex items-center justify-center shrink-0 overflow-hidden">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Icon icon="ph:image-square" className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0 max-w-sm">
                            <h4 className="text-sm font-semibold text-[#0E1726] truncate">
                              {item.name}
                            </h4>
                            {item.description ? (
                              <p className="text-xs text-[#8C8C8C] mt-0.5 line-clamp-1">
                                {item.description}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-400 italic mt-0.5">
                                Tanpa deskripsi
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Ketersediaan Column */}
                      <td className="py-4 px-6 text-center">
                        <span className="text-sm font-semibold text-[#0E1726]">
                          {item.quantity}/{item.quantity}
                        </span>
                      </td>

                      {/* Harga Column */}
                      <td className="py-4 px-6 text-left whitespace-nowrap">
                        <span className="text-sm font-semibold text-[#0E1726]">
                          Rp. {item.price.toLocaleString('id-ID')}
                        </span>
                        <span className="text-xs text-[#8C8C8C] italic font-normal ml-1">/ sesi</span>
                      </td>

                      {/* Status Column */}
                      <td className="py-4 px-6 text-center whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.is_active}
                              onChange={() => handleToggleStatus(item)}
                              className="sr-only peer"
                            />
                            <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]"></div>
                          </label>
                          <span
                            className={`text-xs font-semibold ${
                              item.is_active ? 'text-[#0E1726]' : 'text-gray-400'
                            }`}
                          >
                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </div>
                      </td>

                      {/* Aksi Column */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          {/* Edit Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(item)}
                            className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 text-gray-600 hover:text-[#FC480D] flex items-center justify-center transition-colors shadow-2xs cursor-pointer"
                            title="Edit sewa & layanan"
                            aria-label={`Edit ${item.name}`}
                          >
                            <Icon icon="ph:pencil-simple" className="w-4 h-4" />
                          </button>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenDeleteModal(item)}
                            className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white hover:bg-red-50 text-gray-600 hover:text-red-600 flex items-center justify-center transition-colors shadow-2xs cursor-pointer"
                            title="Hapus sewa & layanan"
                            aria-label={`Hapus ${item.name}`}
                          >
                            <Icon icon="ph:trash" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Card Footer */}
        {services.length > 0 && (
          <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <span className="text-xs text-[#8C8C8C]">
              {services.length} dari {totalItems} item
            </span>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Sebelumnya
                </button>
                <span className="text-xs text-gray-600">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Selanjutnya
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================================== */}
      {/* MODAL: TAMBAH / EDIT SEWA & LAYANAN (Screenshot 2)             */}
      {/* ============================================================== */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs !mt-0">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl relative animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base sm:text-lg font-semibold text-[#0E1726]">
                {formMode === 'create' ? 'Tambah sewa & layanan' : 'Edit sewa & layanan'}
              </h3>
              <button
                type="button"
                onClick={() => setIsFormModalOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Tutup modal"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body Form */}
            <form onSubmit={handleSaveForm} className="space-y-4">
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handlePhotoSelect(f);
                }}
              />

              {/* Photo Box 1:1 and Name Row */}
              <div className="flex items-start gap-4">
                {/* 1:1 Photo Box */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 border-dashed border-[#D5D5D5] bg-[#F6F6F6] hover:bg-gray-100/70 transition-colors relative overflow-hidden flex flex-col items-center justify-center cursor-pointer group shrink-0"
                >
                  {photoPreview || existingImage ? (
                    <>
                      <img
                        src={photoPreview || existingImage!}
                        alt="Foto layanan"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        aria-label="Hapus foto"
                      >
                        <Icon icon="ph:trash-bold" className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-1 text-center pointer-events-none p-1">
                      <Icon icon="ph:image-square" className="w-6 h-6 text-[#98A2B3]" />
                      <span className="text-[10.5px] text-[#919191] font-medium leading-tight">
                        Foto 1:1
                      </span>
                    </div>
                  )}
                </div>

                {/* Name & Hint */}
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    Nama
                  </label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Contoh: Sewa rompi (1 set)"
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
                    required
                  />
                  <p className="text-[11px] text-[#8C8C8C] mt-1.5 leading-relaxed">
                    Foto rasio 1:1, tampil di daftar dan kasir.
                  </p>
                </div>
              </div>

              {/* Deskripsi (opsional) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-[#0E1726]">
                    Deskripsi (opsional)
                  </label>
                  <span className="text-[11px] text-[#8C8C8C]">
                    {formDescription.length}/200
                  </span>
                </div>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value.slice(0, 200))}
                  rows={2}
                  placeholder="Keterangan singkat untuk kasir"
                  className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors h-20 resize-none"
                />
              </div>

              {/* Harga */}
              <div>
                <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Harga</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500">
                    Rp.
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formPrice ? formPrice.toLocaleString('id-ID') : ''}
                    onChange={(e) => {
                      const raw = Number(e.target.value.replace(/\D/g, '')) || 0;
                      setFormPrice(raw);
                    }}
                    placeholder="0"
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white pl-10 pr-3.5 py-2.5 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Jumlah unit tersedia */}
              <div>
                <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                  Jumlah unit tersedia
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    value={formQuantity || ''}
                    onChange={(e) => setFormQuantity(Number(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none transition-colors pr-12"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500">
                    unit
                  </span>
                </div>
              </div>

              {/* Status aktif */}
              <div className="pt-2">
                <div className="flex items-center justify-between p-3.5 rounded-2xl border border-[#E0E6ED] bg-white">
                  <div>
                    <h5 className="text-xs font-semibold text-[#0E1726]">Status aktif</h5>
                    <p className="text-[11px] text-[#8C8C8C] mt-0.5">
                      Tampil di kasir saat tambah transaksi
                    </p>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={formIsActive}
                      onChange={(e) => setFormIsActive(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#F5F6F7]">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="rounded-full px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] disabled:opacity-50 text-white font-semibold text-sm px-7 py-2.5 shadow-xs transition-colors cursor-pointer flex items-center gap-2"
                >
                  {isSubmitting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
                  <span>Simpan</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* MODAL: KONFIRMASI HAPUS (Screenshot 3)                         */}
      {/* ============================================================== */}
      {isDeleteModalOpen && deletingItem && (
        <div
          onClick={() => {
            if (!isDeleting) {
              setIsDeleteModalOpen(false);
              setDeletingItem(null);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs !mt-0"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-[420px] w-full p-3 sm:p-4 shadow-2xl relative text-left animate-in fade-in zoom-in-95"
          >
            <h3 className="text-base sm:text-lg font-semibold text-[#0E1726]">
              Hapus {deletingItem.name}?
            </h3>
            <p className="text-xs sm:text-sm text-[#8C8C8C] leading-relaxed mt-2.5">
              Item akan dihapus dari daftar dan kasir. Riwayat transaksi yang memakai item ini tetap
              tersimpan.
            </p>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeletingItem(null);
                }}
                className="rounded-full px-4 py-2 text-xs sm:text-sm font-semibold text-[#FC480D] hover:bg-orange-50/60 transition-colors cursor-pointer"
              >
                Batal
              </button>

              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                className="rounded-full bg-[#E53935] hover:bg-red-700 disabled:opacity-50 text-white font-semibold text-xs sm:text-sm px-5 sm:px-6 py-2 sm:py-2.5 shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                {isDeleting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
                <span>Ya, hapus</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceList;
